import { OPS_AUTH, USE_MOCK_AUTH } from "../config/api";
import { apiRequest, unwrapApiEnvelope } from "./apiClient";
import {
  clearPendingOpsUser,
  extractTokenFromPayload,
  normalizeOpsUser,
  savePendingOpsUser,
} from "../utils/normalizeOpsUser";
import {
  mockRequestLoginOtp,
  mockVerifyLoginOtp,
  mockResendLoginOtp,
} from "./mockAuth";

/** Identifier for resend/logout — email or phone as registered. */
function toEmailOrPhone(value) {
  const trimmed = String(value ?? "").trim();
  return trimmed.includes("@") ? trimmed.toLowerCase() : trimmed;
}

/**
 * Build login payload for Redux from verify response inner `data`.
 * Verify response is the single source of truth — never merge step-1 pending user.
 */
function buildLoginResult(payload) {
  const token = extractTokenFromPayload(payload);
  const user = normalizeOpsUser(payload);

  if (!user?.email) {
    throw new Error("Unexpected response from login service.");
  }

  if (!token) {
    throw new Error("Login succeeded but no access token was returned.");
  }

  if (!user.role) {
    throw new Error("Login succeeded but no role was returned.");
  }

  if (!Array.isArray(user.permissions)) {
    user.permissions = [];
  }

  return {
    token,
    token_type: payload?.token_type ?? "Bearer",
    expires_in: payload?.expires_in ?? null,
    user,
  };
}

/**
 * Step 1 — POST /api/ops/auth/request
 * Backend returns the operator profile (incl. permissions) and sends OTP.
 */
export async function requestLoginOtp(email) {
  const normalizedEmail = email.trim().toLowerCase();

  if (USE_MOCK_AUTH) {
    const payload = await mockRequestLoginOtp(normalizedEmail);
    const user = normalizeOpsUser(payload);

    if (!user?.email) {
      throw new Error("Unexpected response from login service.");
    }

    savePendingOpsUser(user);

    return {
      email: user.email,
      channel: "email",
      expires_in: 600,
      demo_otp: payload.demo_otp,
      user,
      message: payload.message ?? "Verification code sent.",
    };
  }

  const json = await apiRequest(OPS_AUTH.request, {
    method: "POST",
    body: { email: normalizedEmail },
  });

  const payload = unwrapApiEnvelope(json);
  const user = normalizeOpsUser(payload);

  if (!user?.email) {
    throw new Error("Unexpected response from login service.");
  }

  savePendingOpsUser(user);

  return {
    email: user.email,
    channel: "email",
    expires_in: 600,
    user,
    message: json?.data?.message ?? "Verification code sent.",
  };
}

/**
 * Step 2 — POST /api/ops/user/user/verify
 * Confirm OTP — Redux user comes only from this response.
 */
export async function verifyLoginOtp(email, otp) {
  const normalizedEmail = email.trim().toLowerCase();

  if (USE_MOCK_AUTH) {
    const payload = await mockVerifyLoginOtp(normalizedEmail, otp);
    const result = buildLoginResult(payload);
    clearPendingOpsUser();
    return result;
  }

  const json = await apiRequest(OPS_AUTH.verify, {
    method: "POST",
    body: { email: normalizedEmail, otp: String(otp).trim() },
  });

  const payload = unwrapApiEnvelope(json);
  const result = buildLoginResult(payload);

  clearPendingOpsUser();
  return result;
}

/** Resend OTP — POST /api/ops/user/user/resend-otp */
export async function resendLoginOtp(email) {
  const identifier = toEmailOrPhone(email);

  if (USE_MOCK_AUTH) {
    return mockResendLoginOtp(identifier.includes("@") ? identifier : email);
  }

  const json = await apiRequest(OPS_AUTH.resend, {
    method: "POST",
    body: { emailorPhone: identifier },
  });

  const payload = unwrapApiEnvelope(json);
  const user = normalizeOpsUser(payload);

  if (user) savePendingOpsUser(user);

  return {
    email: user?.email ?? (identifier.includes("@") ? identifier : email),
    channel: "email",
    expires_in: 600,
    user,
    message: json?.data?.message ?? "A new verification code has been sent.",
  };
}

/** Logout — POST /api/ops/logout with Bearer token, no body. */
export async function logoutOpsUser(token) {
  clearPendingOpsUser();

  if (USE_MOCK_AUTH) return;
  if (!token) return;

  try {
    await apiRequest(OPS_AUTH.logout, {
      method: "POST",
      token,
    });
  } catch {
    // Local session is still cleared even if the API call fails.
  }
}

export { USE_MOCK_AUTH };
